import {useCallback, useState} from 'react';
import {useLatest} from 'ahooks';
import {NavigationDirection} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';

export interface UseAttachmentLightbox {
  isOpen: boolean;
  currentIndex: number;
  open: (index: number) => void;
  close: () => void;
  goToPrevious: () => void;
  goToNext: () => void;
}

export const useAttachmentLightbox = (totalCount: number, onIndexChange?: (index: number) => void): UseAttachmentLightbox => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const totalCountRef = useLatest(totalCount);
  const onIndexChangeRef = useLatest(onIndexChange);

  const open = useCallback((index: number): void => {
    if (totalCountRef.current <= 0) {
      return;
    }
    const safeIndex = Math.max(0, Math.min(totalCountRef.current - 1, index));
    setCurrentIndex(safeIndex);
    setIsOpen(true);
    onIndexChangeRef.current?.(safeIndex);
  }, [totalCountRef, onIndexChangeRef]);

  const close = useCallback((): void => {
    setIsOpen(false);
  }, []);

  const navigate = useCallback((direction: NavigationDirection): void => {
    setCurrentIndex((prev) => {
      const next = Math.max(0, Math.min(totalCountRef.current - 1, prev + direction));
      onIndexChangeRef.current?.(next);
      return next;
    });
  }, [totalCountRef, onIndexChangeRef]);

  const goToPrevious = useCallback((): void => {
    navigate(NavigationDirection.PREVIOUS);
  }, [navigate]);

  const goToNext = useCallback((): void => {
    navigate(NavigationDirection.NEXT);
  }, [navigate]);

  return {isOpen, currentIndex, open, close, goToPrevious, goToNext};
};
